/**
 * Promotional discount codes for **subscription** plans.
 *
 * The plans-page coupon box is ON (#943). It no longer validates against a
 * list in the app: the box asks the API (`POST /api/promotion-code/check`),
 * which looks the code up among Stripe's active promotion codes and checks the
 * restrictions Stripe exposes (customer, first purchase, minimum amount, the
 * products the coupon applies to). The answer is a preview, used only to show
 * the discounted price. Checkout resolves the code again server-side and
 * Stripe applies it, so the customer is charged exactly what Checkout shows.
 *
 * A code the customer brought on the registration link (`?coupon=`, kept as
 * `pendingPromoCode`) pre-fills the box.
 *
 * See docs/business/subscription-pricing-display.md.
 */
export const PROMO_CODES_ENABLED = true;

export interface PromoCode {
  /** The code as Stripe stores it, or the sale's display name. */
  code: string;
  /** Short name for the applied-discount chip. */
  label: string;
  /** Percent off list price; 0 when the discount is a fixed amount. */
  percentOff: number;
  /** Fixed amount off, in cents, when the coupon is not a percentage. */
  amountOffCents?: number;
  /**
   * How long the discount lasts before the plan reverts to list price, in
   * months. 0 for a one-off discount on the first payment; Infinity for one
   * that lasts as long as the plan.
   */
  durationMonths: number;
  /** Plan uids the code is valid for. Anything else is shown at list price. */
  appliesToPlanIds: string[];
  /** Shown under the input once the code is accepted. */
  description: string;
}

/** What `POST /api/promotion-code/check` answers (display-safe only). */
export type PromotionCodeCheckResult =
  | {
      valid: true;
      code: string;
      percentOff: number | null;
      amountOff: number | null;
      currency: string | null;
      duration: 'once' | 'repeating' | 'forever';
      durationInMonths: number | null;
      appliesToPlanIds: string[];
      message: string;
    }
  | { valid: false; message: string; reason?: PromotionCodeRefusal };

/**
 * Why a code was not applied (#956), so the coupon box can say "not valid"
 * differently from "try again later". Absent from older API answers, which
 * are treated as `invalid`.
 */
export type PromotionCodeRefusal = 'invalid' | 'rate_limited' | 'unavailable';

/**
 * Turns the API's preview of a valid code into the PromoCode the plan cards
 * price with.
 *
 * @param result A valid check result.
 * @returns The promotion to apply to the cards.
 */
export function promoFromCheck(
  result: Extract<PromotionCodeCheckResult, { valid: true }>,
): PromoCode {
  const durationMonths =
    result.duration === 'forever'
      ? Number.POSITIVE_INFINITY
      : result.duration === 'repeating'
        ? (result.durationInMonths ?? 0)
        : 0;
  return {
    code: result.code,
    label: result.code,
    percentOff: result.percentOff ?? 0,
    ...(result.amountOff !== null ? { amountOffCents: result.amountOff } : {}),
    durationMonths,
    appliesToPlanIds: [...result.appliesToPlanIds],
    description: result.message,
  };
}

/**
 * An automatic sale: shown and applied without a code, to companies that
 * qualify. Display only — the API decides eligibility at checkout and attaches
 * the Stripe coupon (`api/src/shared/promotions.ts`, which must stay in step).
 */
export interface AutomaticSale extends PromoCode {
  /** Billing intervals the sale applies to. */
  billingIntervals: readonly ('monthly' | 'annual')[];
  /** ISO 3166-1 alpha-2 billing countries that qualify. */
  countries: readonly string[];
  /** Inclusive start (ISO 8601, UTC). */
  startsAt: string;
  /** Exclusive end (ISO 8601, UTC). */
  endsAt: string;
  /** Short line for the plans-page banner. */
  bannerText: string;
}

/** October 2026: Standard $1,000 → $600/mo for US companies (#872). */
export const OCTOBER_2026_STANDARD_SALE: AutomaticSale = {
  code: 'OCTOBER SALE',
  label: 'October sale',
  percentOff: 40,
  durationMonths: 12,
  appliesToPlanIds: ['standard'],
  billingIntervals: ['monthly'],
  countries: ['US'],
  startsAt: '2026-10-01T07:00:00Z',
  endsAt: '2026-11-01T07:00:00Z',
  bannerText: 'October sale: Standard is $600/mo instead of $1,000 for US companies. Ends Oct 31.',
  description:
    'October sale: 40% off Standard monthly for your first 12 months, for US companies. ' +
    'Check out by Oct 31, 11:59 PM PT. After 12 months the plan renews at list price.',
};

/** Every automatic sale, checked in order. */
export const AUTOMATIC_SALES: readonly AutomaticSale[] = [OCTOBER_2026_STANDARD_SALE];

/**
 * The sale a company sees for a billing interval right now, or null. Mirrors
 * the API's `findApplicableSale` except for the plan, which each card checks
 * with `promoAppliesToPlan`.
 *
 * @param billingCountry The company's billing country (any case).
 * @param billingInterval The interval being shown.
 * @param now Current time (injectable for tests).
 * @returns The live sale for this company and interval, or null.
 */
export function findActiveSale(
  billingCountry: string | null | undefined,
  billingInterval: 'monthly' | 'annual',
  now: Date = new Date(),
): AutomaticSale | null {
  const country = (billingCountry ?? '').trim().toUpperCase();
  const time = now.getTime();
  return (
    AUTOMATIC_SALES.find(
      (sale) =>
        !!country &&
        sale.countries.includes(country) &&
        sale.billingIntervals.includes(billingInterval) &&
        time >= Date.parse(sale.startsAt) &&
        time < Date.parse(sale.endsAt),
    ) ?? null
  );
}

/** True when the promotion applies to this plan. Enterprise is quoted, never discounted here. */
export function promoAppliesToPlan(
  promo: PromoCode | null,
  planId: string | undefined,
): boolean {
  if (!promo || !planId) return false;
  return promo.appliesToPlanIds.includes(planId);
}

/**
 * Discounted price in cents, rounded to the nearest cent. Returns the list
 * price untouched when the promotion does not apply to this plan, so callers
 * can pass it through unconditionally.
 */
export function applyPromoToCents(
  cents: number,
  promo: PromoCode | null,
  planId?: string,
): number {
  if (!promoAppliesToPlan(promo, planId)) return cents;
  if (promo!.amountOffCents) {
    return Math.max(0, cents - promo!.amountOffCents);
  }
  return Math.round(cents * (1 - promo!.percentOff / 100));
}
